import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  Divider,
  Tooltip,
  useTheme,
  LinearProgress,
  Paper,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import CropIcon from "@mui/icons-material/Crop";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import VideoPlayer from "./VideoPlayer";
import VideoControls from "./VideoControls";
import VideoTimeline from "./VideoTimeline";
import VideoTrimInfo from "./VideoTrimInfo";
import VideoCropControls from "./VideoCropControls";
import { CropRect } from "./VideoCropOverlay";
import CropOptionsDialog, { CropQualityOption } from "./CropOptionsDialog";

interface VideoEditorDialogProps {
  open: boolean;
  onClose: () => void;
  videoFile: File;
  onEditComplete: (processedFile: File) => void;
}

interface EncodingPreset {
  crf: string;
  preset: string;
  description: string;
}

const ENCODING_PRESETS: Record<CropQualityOption, EncodingPreset> = {
  fast: {
    crf: "28",
    preset: "ultrafast",
    description: "Fast processing (lower quality)",
  },
  balanced: {
    crf: "23",
    preset: "medium",
    description: "Balanced quality and speed",
  },
  highQuality: {
    crf: "18",
    preset: "slow",
    description: "High quality (slower processing)",
  },
};

const VideoEditorDialog: React.FC<VideoEditorDialogProps> = ({
  open,
  onClose,
  videoFile,
  onEditComplete,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [videoSrc, setVideoSrc] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [ffmpeg, setFfmpeg] = useState<FFmpeg>(new FFmpeg());
  const [showCropOptions, setShowCropOptions] = useState(false);
  const [cropQualityOption, setCropQualityOption] =
    useState<CropQualityOption>("balanced");
  const [progress, setProgress] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [cropModeEnabled, setCropModeEnabled] = useState(false);

  const [cropRect, setCropRect] = useState<CropRect>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      setLoading(true);
      const ffmpegInstance = new FFmpeg();
      setFfmpeg(ffmpegInstance);

      try {
        const url = URL.createObjectURL(videoFile);
        setVideoSrc(url);

        const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.2/dist/umd";
        await ffmpegInstance.load({
          coreURL: await toBlobURL(
            `${baseURL}/ffmpeg-core.js`,
            "text/javascript",
          ),
          wasmURL: await toBlobURL(
            `${baseURL}/ffmpeg-core.wasm`,
            "application/wasm",
          ),
        });

        ffmpegInstance.on("progress", ({ progress }) => {
          setProgress(Math.round(progress * 100));
        });
      } catch (error) {
        console.error("Error loading FFmpeg:", error);
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      if (videoSrc) URL.revokeObjectURL(videoSrc);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [open, videoFile]);

  const checkIfCroppingNeeded = () => {
    return (
      cropModeEnabled &&
      (cropRect.width !== videoSize.width ||
        cropRect.height !== videoSize.height ||
        cropRect.x !== 0 ||
        cropRect.y !== 0)
    );
  };

  const handleApplyChanges = () => {
    if (checkIfCroppingNeeded()) {
      setShowCropOptions(true);
    } else {
      processVideo(cropQualityOption);
    }
  };

  const handleCropOptionsConfirm = (option: CropQualityOption) => {
    setCropQualityOption(option);
    setShowCropOptions(false);
    processVideo(option);
  };

  const handleCancelProcessing = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setProcessing(false);
      setProgress(0);
    }
  };

  const handleToggleCropMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCropModeEnabled(event.target.checked);

    if (event.target.checked && videoSize.width > 0 && videoSize.height > 0) {
      setCropRect({
        x: 0,
        y: 0,
        width: videoSize.width,
        height: videoSize.height,
      });
    }
  };

  const processVideo = async (qualityOption: CropQualityOption) => {
    if (!ffmpeg.loaded) return;

    setProcessing(true);
    setProgress(0);

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      if (signal.aborted) throw new Error("Processing was cancelled");

      const ext = videoFile.name.split(".").pop() || "mp4";
      const inputName = `input.${ext}`;
      const baseName = videoFile.name;

      const hasTrimming = trimStart > 0 || trimEnd < duration;
      const hasCropping = checkIfCroppingNeeded();
      let outputName = baseName;
      if (hasCropping && hasTrimming) {
        outputName = `edited_${baseName}`;
      } else if (hasCropping) {
        outputName = `cropped_${baseName}`;
      } else if (hasTrimming) {
        outputName = `trimmed_${baseName}`;
      }

      await ffmpeg.writeFile(inputName, await fetchFile(videoFile));

      if (hasTrimming && !hasCropping) {
        const trimDuration = trimEnd - trimStart;
        await ffmpeg.exec([
          "-ss",
          trimStart.toString(),
          "-i",
          inputName,
          "-t",
          trimDuration.toString(),
          "-c",
          "copy",
          outputName,
        ]);
      } else {
        const cmd: string[] = ["-i", inputName];

        if (hasTrimming) {
          cmd.push(
            "-ss",
            trimStart.toString(),
            "-t",
            (trimEnd - trimStart).toString(),
          );
        }

        if (hasCropping) {
          const { crf, preset } = ENCODING_PRESETS[qualityOption];
          cmd.push(
            "-vf",
            `crop=${Math.round(cropRect.width)}:${Math.round(cropRect.height)}:${Math.round(cropRect.x)}:${Math.round(cropRect.y)}`,
            "-c:v",
            "libx264",
            "-crf",
            crf,
            "-preset",
            preset,
            "-c:a",
            "copy",
          );
        } else {
          cmd.push("-c", "copy");
        }

        cmd.push(outputName);
        await ffmpeg.exec(cmd);
      }

      if (signal.aborted) throw new Error("Processing was cancelled");

      const data = await ffmpeg.readFile(outputName);
      const editedFile = new File([data], outputName, { type: videoFile.type });

      onEditComplete(editedFile);
    } catch (error) {
      console.error("Error processing video:", error);
    } finally {
      if (!signal.aborted) {
        setProcessing(false);
        setProgress(0);
        abortControllerRef.current = null;
      }
    }
  };

  const handleVideoMetadata = (videoDuration: number) => {
    setDuration(videoDuration);
    setTrimEnd(videoDuration);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={processing ? undefined : onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Video Editor</Typography>
            <Chip
              label="BETA"
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                borderRadius: 1,
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#aaa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ContentCutIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">Trim</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <CropIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">Crop</Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loading ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="400px"
              gap={2}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading video editor...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {processing && (
                <Paper
                  elevation={3}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "90%",
                    maxWidth: "400px",
                    zIndex: 10,
                    p: 3,
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="500">
                      Processing Video
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {progress}% Complete
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={handleCancelProcessing}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Paper>
              )}

              <VideoPlayer
                videoSrc={videoSrc}
                currentTime={currentTime}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                setCurrentTime={setCurrentTime}
                trimStart={trimStart}
                trimEnd={trimEnd}
                onMetadataLoaded={handleVideoMetadata}
                cropMode={cropModeEnabled}
                cropRect={cropRect}
                setCropRect={setCropRect}
                setVideoSize={setVideoSize}
              />

              <Box sx={{ p: 2 }}>
                <VideoControls
                  isPlaying={isPlaying}
                  setIsPlaying={setIsPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  trimStart={trimStart}
                  setCurrentTime={setCurrentTime}
                />
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <ContentCutIcon fontSize="small" sx={{ mr: 0.5 }} /> Trim
                    Settings
                  </Typography>
                  <VideoTimeline
                    duration={duration}
                    currentTime={currentTime}
                    trimStart={trimStart}
                    trimEnd={trimEnd}
                    setTrimStart={setTrimStart}
                    setTrimEnd={setTrimEnd}
                    setCurrentTime={setCurrentTime}
                  />
                  <VideoTrimInfo
                    trimStart={trimStart}
                    trimEnd={trimEnd}
                    duration={duration}
                  />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <CropIcon fontSize="small" sx={{ mr: 0.5 }} /> Crop
                      Settings
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={cropModeEnabled}
                          onChange={handleToggleCropMode}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">Enable Crop</Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  </Box>
                  {cropModeEnabled && (
                    <VideoCropControls
                      cropRect={cropRect}
                      setCropRect={setCropRect}
                      videoWidth={videoSize.width}
                      videoHeight={videoSize.height}
                      disabled={!cropModeEnabled}
                    />
                  )}
                  {!cropModeEnabled && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Enable crop mode to adjust the crop area.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: theme.palette.grey[50] }}>
          <Button
            onClick={onClose}
            disabled={processing}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Tooltip title="Apply both trim and crop settings to the video">
            <Button
              onClick={handleApplyChanges}
              variant="contained"
              disabled={loading || processing}
              sx={{ borderRadius: 2, minWidth: 120 }}
            >
              {processing ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Processing...
                </>
              ) : (
                "Apply Changes"
              )}
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <CropOptionsDialog
        open={showCropOptions}
        onClose={() => setShowCropOptions(false)}
        onConfirm={handleCropOptionsConfirm}
      />
    </>
  );
};

export default VideoEditorDialog;
